// Forecast — monthly loop orchestrator. Pure function: same inputs, same rows.
//
// Each row contains the per-month numbers for the website + PDF + Excel:
//   - App accounts, Cardholders by tier, active users
//   - GMV by tier, total
//   - Per-tier revenue lines (sub, FX, swap, top-up, earn, agentic, interchange)
//   - Per-tier subsidy / cost lines (cashback, FX absorption, agentic compute)
//   - Wirex platform floor and dispute costs
//   - CAC, fixed opex
//   - Contribution profit, operating profit, cumulative cash
//
// Modules called per month:
//   regions.js → blended regional rates
//   interchange.js → Wirex year-share × regional gross
//   fx.js → per-tier FX line
//   stablecoin.js → per-tier Invest yield + wallet operation subsidy
//   cashback.js → per-tier Pulsar Points expense
//   agentic.js → AI agentic lines
//   b2b.js → partner campaigns / closed-loop / loyalty white label
//   costs.js → fixed opex + Wirex floor

import { tierKeys, getTier } from './pricing.js';
import { effectiveInterchange } from './interchange.js';
import { fxPerUser } from './fx.js';
import { cashbackPerUser, blendedCostPerPoint } from './cashback.js';
import { agenticPerUser } from './agentic.js';
import { b2bRevenue } from './b2b.js';
import { wirexDisputeCost, wirexFloorShortfall } from './costs.js';
import { cardProductionCost } from './cardProduction.js';
import { estimateNewReferrals, computeReferralCost, allocateReferralsByTier } from './referrals.js';
import { stablecoinPerUser } from './stablecoin.js';
import { blendedAppAccountCac, cardActivationCost } from './acquisition.js';
import { dropScenarioModifiers, computeDropPrograms } from './drops.js';

const MONTHS = 36;

// Share of top-up volume that flows through the expensive card-to-card rail
// (1.5% acquirer cost). The rest flows via SEPA/ACH/regional rails or wallet
// funding. Wallet operations are costed separately in stablecoin.js.
// inputs.cardFundedTopupShare if provided, else this default.
const DEFAULT_CARD_FUNDED_TOPUP_SHARE = 0.1;

function tierSpend(tier, scenario) {
  if (tier.key === 'free') return scenario.freeSpend;
  if (tier.key === 'pro') return scenario.proSpend;
  return 0;
}

function tierTrading(tier, scenario) {
  if (tier.key === 'free') return scenario.freeTrading;
  if (tier.key === 'pro') return scenario.proTrading;
  return 0;
}

function tierTopup(tier, scenario) {
  if (tier.key === 'free') return scenario.freeTopup;
  if (tier.key === 'pro') return scenario.proTopup;
  return 0;
}

function tierChurn(tier, scenario) {
  if (tier.key === 'free') return scenario.churnFree;
  if (tier.key === 'pro') return scenario.churnPro;
  return 0;
}

function allocateNewCardholders(netNewAppAccounts, scenario) {
  const activated = netNewAppAccounts * scenario.activeRate * scenario.cardActivationRate;
  const pro = activated * scenario.proConv;
  const free = Math.max(0, activated - pro);
  return { free, pro };
}

/**
 * Run the 36-month forecast for the given inputs and scenario key.
 *
 * @param {object} inputs       validated PulsarInputs
 * @param {'conservative'|'base'|'bull'} scenarioKey
 * @returns {Array<object>}     36 row objects
 */
export function runForecast(inputs, scenarioKey = 'base') {
  const scenario = inputs.scenarios[scenarioKey];
  if (!scenario) throw new Error(`Unknown scenario "${scenarioKey}"`);

  const keys = tierKeys(inputs);
  const rows = [];
  const effectiveScenario = dropScenarioModifiers(inputs, scenario);

  // Bootstrap app-account and cardholder state. Net-new in scenarios means
  // App Accounts; only the activated subset becomes Cardholders and generates
  // neobank revenue/cost.
  let appAccounts = inputs.startingUsers;
  const startingActivated =
    inputs.startingUsers * effectiveScenario.activeRate * effectiveScenario.cardActivationRate;
  const usersByTier = {
    free: startingActivated * (1 - inputs.startingProMix),
    pro: startingActivated * inputs.startingProMix,
  };

  let cumGmv = inputs.startingCumGmv;
  let cumCash = 0;
  let pendingMoneyBackAppAccounts = 0;

  // Referral cohorts — each entry { month, referrals: {free, pro} }.
  // Forecast iterates over still-active cohorts each month to compute the
  // 1%-of-spend ongoing share that decays out at month 12.
  const referralCohorts = [];

  const blendedPointCost = blendedCostPerPoint(inputs.points);

  for (let m = 1; m <= MONTHS; m++) {
    // Net new App Accounts this month — linear ramp M1 → M36
    const baseNetNew =
      scenario.netNewM1 + (scenario.netNewM36 - scenario.netNewM1) * ((m - 1) / (MONTHS - 1));
    const moneyBackGeneratedAppAccounts = pendingMoneyBackAppAccounts;
    const netNew = baseNetNew + moneyBackGeneratedAppAccounts;
    pendingMoneyBackAppAccounts = 0;
    appAccounts += netNew;
    const activeAppAccounts = appAccounts * effectiveScenario.activeRate;

    // Activate a subset of net-new App Accounts into Cardholders. Paid
    // conversion applies only after card activation.
    const newCardholdersByTier = allocateNewCardholders(netNew, effectiveScenario);
    const newPro = newCardholdersByTier.pro;
    const newFree = newCardholdersByTier.free;
    const newCardholders = newFree + newPro;

    // Referrals — register a new cohort if any of this month's net-new came
    // from the referral channel. Cohort retains the per-tier mix for the
    // 12-month ongoing-share calculation.
    const newReferrals = estimateNewReferrals(inputs, netNew);
    if (newReferrals > 0) {
      referralCohorts.push({
        month: m,
        referrals: allocateReferralsByTier(newReferrals, effectiveScenario),
      });
    }

    // Apply cardholder churn
    usersByTier.free = Math.max(
      0,
      usersByTier.free * (1 - tierChurn({ key: 'free' }, effectiveScenario)) + newFree,
    );
    usersByTier.pro = Math.max(
      0,
      usersByTier.pro * (1 - tierChurn({ key: 'pro' }, effectiveScenario)) + newPro,
    );
    const totalUsers = usersByTier.free + usersByTier.pro;

    // The tier state represents active Cardholders, because activation is
    // already gated by active App Accounts.
    const activeByTier = { ...usersByTier };
    const activeUsers = totalUsers;

    // GMV by tier, total
    let gmvByTier = { free: 0, pro: 0 };
    for (const k of keys) {
      const tier = getTier(inputs, k);
      gmvByTier[k] = activeByTier[k] * tierSpend(tier, effectiveScenario);
    }
    const gmvTotal = gmvByTier.free + gmvByTier.pro;

    // Effective interchange this month (Wirex year-share × regional blend)
    const ic = effectiveInterchange(inputs, m, cumGmv, gmvTotal);

    // Per-tier revenue and cost line items
    const lines = {
      revenue: {
        subscription: 0,
        interchange: 0,
        fx: 0,
        topup: 0,
        swap: 0,
        earn: 0,
        aiOverage: 0,
        aiOutcome: 0,
        aiReferral: 0,
        b2bPartnerCampaigns: 0,
        b2bClosedLoop: 0,
        b2bWhiteLabel: 0,
      },
      cost: {
        cashbackPoints: 0,
        fxAbsorbed: 0,
        aiCompute: 0,
        wirexFloor: 0,
        disputes: 0,
        walletOperations: 0,
        topupFunding: 0,
      },
      perTier: {},
    };

    let wirexEarnedEur = 0;
    let stablecoinInvestBalance = 0;
    let userEarnYield = 0;
    let networkCostPassThrough = 0;

    for (const k of keys) {
      const tier = getTier(inputs, k);
      const active = activeByTier[k];
      const spend = tierSpend(tier, effectiveScenario);
      const trading = tierTrading(tier, effectiveScenario);
      const topup = tierTopup(tier, effectiveScenario);

      // Subscription
      const subRev = active * tier.price;

      // Interchange — Pulsar's effective capture × tier GMV
      const icRev = gmvByTier[k] * ic.pulsarRate;
      // Wirex's complement
      const wirexInterchangeShare = gmvByTier[k] * ic.grossRate * (1 - ic.wirexShare);

      // FX (per-active-user × active count)
      const fx = fxPerUser(inputs, tier, effectiveScenario, m);
      const fxRev = active * fx.rev;
      const fxCost = active * fx.cost;
      const fxVolUsd = active * fx.fxVol; // EUR
      // Wirex passthrough on the FX volume Pulsar absorbed
      wirexEarnedEur += wirexInterchangeShare + fxVolUsd * inputs.wirex.fxPassThrough;

      // Top-up — only the card-funded portion costs Pulsar (acquirer 1.5%);
      // SEPA/ACH/regional rails and crypto deposits are ~free. We split topup
      // volume by cardFundedShare so the subsidy line reflects reality.
      const cardFundedShare = inputs.cardFundedTopupShare ?? DEFAULT_CARD_FUNDED_TOPUP_SHARE;
      const cardFundedVol = topup * cardFundedShare;
      const topupAbove = Math.max(0, cardFundedVol - tier.topupCap);
      const topupRev = active * topupAbove * tier.topupAboveRate;
      const topupCost = active * Math.min(cardFundedVol, tier.topupCap) * 0.015;

      // Swap (Wirex Exchange) — above cap × rate
      const swapAbove = Math.max(0, trading - tier.swapCap);
      const swapRev = active * swapAbove * tier.swapAboveRate;

      // Stablecoin Invest — Pulsar yield spread minus wallet operation subsidy
      const stable = stablecoinPerUser(inputs, tier, effectiveScenario, k);
      const earnRev = active * stable.pulsarYieldMonthly;
      const walletOperationCost = active * stable.walletOperationCost;
      stablecoinInvestBalance += active * stable.investBalance;
      userEarnYield += active * stable.userYieldMonthly;
      networkCostPassThrough += active * stable.networkCostPassThrough;

      // Cashback (Pulsar Points)
      const cashback = cashbackPerUser(inputs, tier, spend);
      const cashbackCost = active * cashback.realCost;
      // Plus Earn's Pulsar Points bonus liability priced at blended cost
      const earnPointsCost =
        (active * stable.investBalance * (tier.earnApyPointsBonus ?? 0) * blendedPointCost) /
        12 /
        inputs.points.pointFaceValue;

      // Agentic
      const ag = agenticPerUser(inputs, tier, effectiveScenario, k);
      const agOverage = active * ag.overageRev;
      const agOutcome = active * ag.outcomeRev;
      const agReferral = active * ag.referralRev;
      const agCost = active * ag.cost;

      // Aggregate
      lines.revenue.subscription += subRev;
      lines.revenue.interchange += icRev;
      lines.revenue.fx += fxRev;
      lines.revenue.topup += topupRev;
      lines.revenue.swap += swapRev;
      lines.revenue.earn += earnRev;
      lines.revenue.aiOverage += agOverage;
      lines.revenue.aiOutcome += agOutcome;
      lines.revenue.aiReferral += agReferral;

      lines.cost.cashbackPoints += cashbackCost + earnPointsCost;
      lines.cost.fxAbsorbed += fxCost;
      lines.cost.aiCompute += agCost;
      lines.cost.walletOperations += walletOperationCost;
      lines.cost.topupFunding += topupCost;

      // Per-tier P&L row for the per-tier sheet
      const perTierRev =
        subRev + icRev + fxRev + topupRev + swapRev + earnRev + agOverage + agOutcome + agReferral;
      const perTierCost =
        cashbackCost + earnPointsCost + fxCost + topupCost + agCost + walletOperationCost;
      lines.perTier[k] = {
        active,
        gmv: gmvByTier[k],
        rev: perTierRev,
        cost: perTierCost,
        net: perTierRev - perTierCost,
        netPerActive: active > 0 ? (perTierRev - perTierCost) / active : 0,
        investBalance: active * stable.investBalance,
        walletOperationCost,
        stablecoinYieldRev: earnRev,
      };
    }

    // Wirex platform floor and dispute handling
    const wirexFloor = wirexFloorShortfall(inputs, m, wirexEarnedEur);
    lines.cost.wirexFloor = wirexFloor;
    lines.cost.disputes = wirexDisputeCost(inputs, activeUsers);

    // Card production — Pulsar's COGS for new cards issued this month
    const cardCost = cardProductionCost(inputs, {
      free: newFree,
      pro: newPro,
    });
    lines.cost.cardProduction = cardCost;

    // Referral program — activation bonuses for new referrals + ongoing share
    // for active cohorts (decays out at month 12 per cohort).
    const tierSpendMap = {
      free: effectiveScenario.freeSpend,
      pro: effectiveScenario.proSpend,
    };
    const refCost = computeReferralCost(inputs, newReferrals, referralCohorts, m, tierSpendMap);
    lines.cost.referral = refCost.totalCost;

    // Money Back — optional growth/reward program. Defaults are zero, so
    // these lines preserve the baseline until an operator turns a program on.
    const drops = computeDropPrograms(inputs, {
      freeUsers: usersByTier.free,
      proUsers: usersByTier.pro,
      activeCardholders: activeUsers,
      stablecoinYieldRev: lines.revenue.earn,
    });
    pendingMoneyBackAppAccounts = drops.moneyBackGeneratedAppAccounts;
    lines.cost.moneyBack = drops.moneyBackCost;

    // B2B vertical — partner campaigns, closed-loop payments, loyalty white label.
    const b2b = b2bRevenue(inputs, m, scenarioKey);
    lines.revenue.b2bPartnerCampaigns = b2b.partnerCampaigns;
    lines.revenue.b2bClosedLoop = b2b.closedLoop;
    lines.revenue.b2bWhiteLabel = b2b.whiteLabel;

    // Acquisition and fixed operating cost
    const appAccountCac = netNew * blendedAppAccountCac(inputs);
    const cardActivation = cardActivationCost(inputs, newCardholdersByTier);
    const acquisitionCost = appAccountCac + cardActivation;
    const fixedOpexCost = inputs.fixedOpexMonthly ?? 0;

    // Totals
    const totalRev =
      lines.revenue.subscription +
      lines.revenue.interchange +
      lines.revenue.fx +
      lines.revenue.topup +
      lines.revenue.swap +
      lines.revenue.earn +
      lines.revenue.aiOverage +
      lines.revenue.aiOutcome +
      lines.revenue.aiReferral +
      lines.revenue.b2bPartnerCampaigns +
      lines.revenue.b2bClosedLoop +
      lines.revenue.b2bWhiteLabel;
    const variableProductCost =
      lines.cost.cashbackPoints +
      lines.cost.fxAbsorbed +
      lines.cost.aiCompute +
      lines.cost.topupFunding +
      lines.cost.wirexFloor +
      lines.cost.disputes +
      lines.cost.cardProduction +
      lines.cost.referral +
      lines.cost.walletOperations +
      lines.cost.moneyBack;
    const contributionProfit = totalRev - variableProductCost;
    const totalCost = variableProductCost + acquisitionCost + fixedOpexCost;
    const netPL = totalRev - totalCost;
    cumCash += netPL;
    cumGmv += gmvTotal;

    rows.push({
      month: m,
      // Funnel
      baseNewAppAccounts: baseNetNew,
      moneyBackGeneratedAppAccounts,
      newAppAccounts: netNew,
      appAccounts,
      activeAppAccounts,
      newCardholders,
      totalUsers,
      totalCardholders: totalUsers,
      freeUsers: usersByTier.free,
      proUsers: usersByTier.pro,
      freeCardholders: usersByTier.free,
      proCardholders: usersByTier.pro,
      activeUsers,
      activeCardholders: activeUsers,
      cardActivationRate: effectiveScenario.cardActivationRate,
      // GMV
      gmvTotal,
      gmvFree: gmvByTier.free,
      gmvPro: gmvByTier.pro,
      cumGmv,
      // Interchange diagnostics
      interchangeGrossRate: ic.grossRate,
      wirexShare: ic.wirexShare,
      pulsarRate: ic.pulsarRate,
      // Revenue lines (totals)
      proSubRev: usersByTier.pro * inputs.tiers.pro.price,
      interchangeRev: lines.revenue.interchange,
      fxRev: lines.revenue.fx,
      topupRev: lines.revenue.topup,
      swapRev: lines.revenue.swap,
      earnRev: lines.revenue.earn,
      stablecoinYieldRev: lines.revenue.earn,
      stablecoinInvestBalance,
      userEarnYield,
      aiOverageRev: lines.revenue.aiOverage,
      aiOutcomeRev: lines.revenue.aiOutcome,
      aiReferralRev: lines.revenue.aiReferral,
      b2bPartnerCampaignsRev: lines.revenue.b2bPartnerCampaigns,
      b2bClosedLoopRev: lines.revenue.b2bClosedLoop,
      b2bWhiteLabelRev: lines.revenue.b2bWhiteLabel,
      totalRev,
      // Cost lines
      cashbackPointsCost: lines.cost.cashbackPoints,
      fxAbsorbedCost: lines.cost.fxAbsorbed,
      aiComputeCost: lines.cost.aiCompute,
      topupFundingCost: lines.cost.topupFunding,
      wirexFloorCost: lines.cost.wirexFloor,
      disputeCost: lines.cost.disputes,
      cardProductionCost: lines.cost.cardProduction,
      referralCost: lines.cost.referral,
      moneyBackCost: lines.cost.moneyBack,
      moneyBackPartnerFundedValue: drops.moneyBackPartnerFundedValue,
      moneyBackUserRewardValue: drops.moneyBackUserRewardValue,
      moneyBackWinners: drops.moneyBackWinners,
      moneyBackGeneratedNextAppAccounts: drops.moneyBackGeneratedAppAccounts,
      walletOperationCost: lines.cost.walletOperations,
      networkCostPassThrough,
      appAccountCacCost: appAccountCac,
      cardActivationCost: cardActivation,
      acquisitionCost,
      fixedOpexCost,
      variableProductCost,
      totalCost,
      // P&L
      contributionProfit,
      operatingProfit: netPL,
      netStablecoinEconomics: lines.revenue.earn - lines.cost.walletOperations,
      netPL,
      cumCash,
      // Per-tier roll-up for sensitivity / per-tier P&L sheet
      perTier: lines.perTier,
    });
  }

  return rows;
}
